import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TopBar, type Tab } from "../components/TopBar";
import { Unauthorized } from "../lib/api";
import {
  PILLARS,
  PILLAR_COLORS,
  pct,
  pillarsApi,
  type IcWeights,
  type Pillar,
  type PillarMeta,
  type PillarRank,
  type ViewFilters,
  type Weights,
} from "../lib/pillars";
import { BacktestView } from "./pillars/BacktestView";
import { ChartsView } from "./pillars/ChartsView";
import { DataView } from "./pillars/DataView";
import { PairsView } from "./pillars/PairsView";
import { RankingsView } from "./pillars/RankingsView";

type View = "rankings" | "charts" | "backtest" | "pairs" | "data";
const VIEWS: { key: View; label: string }[] = [
  { key: "rankings", label: "Rankings" },
  { key: "charts", label: "Charts" },
  { key: "backtest", label: "Backtest" },
  { key: "pairs", label: "Pairs" },
  { key: "data", label: "Data" },
];

const EQUAL: Weights = { value: 20, quality: 20, growth: 20, momentum: 20, risk: 20 };
const select =
  "w-full bg-term-bg border border-term-edge rounded px-2 py-1 outline-none focus:border-term-accent";
const label = "block text-[10px] uppercase tracking-wide text-term-dim mb-1";

/**
 * The Pillars tab: every company scored on five pillars, ranked by a blend the
 * reader controls.
 *
 * The left rail holds what defines the ranking — date, sub-universe, weights — and it
 * is shared by every view, so the charts, the backtest and the pairs scan always
 * describe the same thing the table does.
 */
export function Pillars({
  user,
  onLogout,
  tab,
  onTab,
}: {
  user: string;
  onLogout: () => void;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  const [meta, setMeta] = useState<PillarMeta | null>(null);
  const [icw, setIcw] = useState<IcWeights | null>(null);
  const [on, setOn] = useState("");
  const [weights, setWeights] = useState<Weights>(EQUAL);
  const [filters, setFilters] = useState<ViewFilters>({ sector: "", size: "", index: "" });
  const [view, setView] = useState<View>("rankings");
  const [data, setData] = useState<PillarRank | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof Unauthorized) onLogout();
      else setError(e instanceof Error ? e.message : String(e));
    },
    [onLogout],
  );

  useEffect(() => {
    pillarsApi
      .meta()
      .then((m) => {
        setMeta(m);
        setOn(m.dates[m.dates.length - 1] ?? "");
      })
      .catch(fail);
    pillarsApi.icWeights().then(setIcw).catch(() => {});
  }, [fail]);

  const latest = meta?.dates[meta.dates.length - 1] ?? "";
  const isLatest = on === latest;

  // An index list says who is in the index TODAY; on any earlier date it is
  // survivorship bias, and the API refuses it. Drop the filter instead of erroring.
  useEffect(() => {
    if (!isLatest && filters.index) setFilters((f) => ({ ...f, index: "" }));
  }, [isLatest, filters.index]);

  // Debounced: a slider being dragged is not thirty requests.
  const seq = useRef(0);
  useEffect(() => {
    if (!on) return;
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      setLoading(true);
      pillarsApi
        .rank(on, weights, isLatest ? filters : { ...filters, index: "" })
        .then((r) => {
          if (mine !== seq.current) return; // a newer request superseded this one
          setData(r);
          setError(null);
        })
        .catch(fail)
        .finally(() => mine === seq.current && setLoading(false));
    }, 180);
    return () => clearTimeout(timer);
  }, [on, weights, filters, isLatest, fail]);

  const total = useMemo(() => PILLARS.reduce((s, p) => s + weights[p], 0), [weights]);
  const toggle = useCallback(
    (cik: number) =>
      setSelected((s) => (s.includes(cik) ? s.filter((c) => c !== cik) : [...s, cik].slice(-8))),
    [],
  );

  if (meta && !meta.panel) {
    return (
      <div className="h-full flex flex-col">
        <TopBar user={user} onLogout={onLogout} tab={tab} onTab={onTab} />
        <div className="flex-1 grid place-items-center text-term-dim">
          <p>
            The pillar panel has not been built. Run <span className="text-term-text">srank pillar-panel</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <TopBar user={user} onLogout={onLogout} tab={tab} onTab={onTab} />
      <div className="flex-1 min-h-0 flex">
        <aside className="w-[300px] shrink-0 border-r border-term-edge bg-term-panel overflow-y-auto p-2 space-y-3">
          <div>
            <label className={label}>As known on (month-end)</label>
            <select value={on} onChange={(e) => setOn(e.target.value)} className={`${select} tnum`}>
              {[...(meta?.dates ?? [])].reverse().map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {meta?.prices_through && (
              <p className="mt-1 text-[10px] leading-relaxed text-term-dim">
                Prices held through <span className="tnum">{meta.prices_through}</span>.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div>
              <label className={label}>Index (today's members, latest date only)</label>
              <select
                value={filters.index}
                disabled={!isLatest}
                onChange={(e) => setFilters({ ...filters, index: e.target.value })}
                className={`${select} disabled:opacity-40`}
              >
                <option value="">Whole investable universe</option>
                {meta?.indexes.map((i) => (
                  <option key={i.key} value={i.key}>
                    {i.label} (as of {i.as_of})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={label}>Sector</label>
                <select
                  value={filters.sector}
                  onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                  className={select}
                >
                  <option value="">All</option>
                  {meta?.sectors.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className={label}>Size</label>
                <select
                  value={filters.size}
                  onChange={(e) => setFilters({ ...filters, size: e.target.value })}
                  className={select}
                >
                  <option value="">All</option>
                  {meta?.sizes.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className={label}>Pillar weights</label>
            <div className="space-y-2">
              {PILLARS.map((p) => (
                <WeightSlider
                  key={p}
                  pillar={p}
                  value={weights[p]}
                  share={total > 0 ? weights[p] / total : 0}
                  factors={meta?.pillars.find((x) => x.name === p)?.factors.map((f) => f.name) ?? []}
                  onChange={(v) => setWeights({ ...weights, [p]: v })}
                />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(meta?.presets ?? {}).map(([name, w]) => (
                <Chip key={name} onClick={() => setWeights(toSliders(w))}>
                  {name.replace(/_/g, " ")}
                </Chip>
              ))}
              {icw && (
                <Chip onClick={() => setWeights(toSliders(icw.current))} title={`From the last ${icw.lookback} months of realised pillar ICs, as of ${icw.as_of}`}>
                  IC-weighted
                </Chip>
              )}
            </div>
            {total === 0 && (
              <p className="mt-2 text-[10px] text-term-down">At least one pillar needs weight.</p>
            )}
            <p className="mt-2 text-[10px] leading-relaxed text-term-dim">
              Weights are a preference, not a finding. They carry no track record until you
              backtest them — and every weighting backtested raises the bar the next one must clear.
            </p>
          </div>
        </aside>

        <main className="flex-1 min-w-0 flex flex-col">
          <div className="h-8 shrink-0 flex items-center gap-1 px-2 border-b border-term-edge text-[11px]">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`px-2 py-1 rounded ${
                  view === v.key ? "bg-term-accent/20 text-term-accent" : "text-term-dim hover:text-term-text"
                }`}
              >
                {v.label}
              </button>
            ))}
            <div className="flex-1" />
            {data && (
              <span className="text-term-dim tnum">
                {data.n_ranked.toLocaleString()} names · {data.on}
                {loading ? " · …" : ` · ${data.elapsed_ms}ms`}
              </span>
            )}
          </div>
          {error && <div className="px-3 py-2 text-term-down text-[11px]">{error}</div>}
          <div className="flex-1 min-h-0 overflow-auto">
            {view === "rankings" && data && (
              <RankingsView data={data} selected={selected} onToggle={toggle} />
            )}
            {view === "charts" && data && <ChartsView data={data} selected={selected} />}
            {view === "backtest" && (
              <BacktestView weights={total > 0 ? weights : null} icWeights={icw} onError={fail} />
            )}
            {view === "pairs" && <PairsView filters={filters} onError={fail} />}
            {view === "data" && <DataView onError={fail} />}
          </div>
        </main>
      </div>
    </div>
  );
}

function toSliders(w: Weights): Weights {
  const out = { ...EQUAL };
  for (const p of PILLARS) out[p] = Math.round(w[p] * 100);
  return out;
}

function WeightSlider({
  pillar,
  value,
  share,
  factors,
  onChange,
}: {
  pillar: Pillar;
  value: number;
  share: number;
  factors: string[];
  onChange: (v: number) => void;
}) {
  return (
    <div title={factors.join(" + ")}>
      <div className="flex items-baseline justify-between text-[11px]">
        <span style={{ color: PILLAR_COLORS[pillar] }}>{pillar}</span>
        <span className="tnum text-term-text">{pct(share, 0)}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1 accent-term-accent"
        style={{ accentColor: PILLAR_COLORS[pillar] }}
      />
      <div className="text-[9px] text-term-dim truncate">{factors.join(" + ").replace(/_/g, " ")}</div>
    </div>
  );
}

function Chip({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="px-2 py-0.5 rounded border border-term-edge text-[10px] text-term-dim hover:text-term-text hover:border-term-accent"
    >
      {children}
    </button>
  );
}
